import type { ApexOptions } from 'apexcharts';
import { useEffect, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { type ColumnDefinition, ReactTabulator } from 'react-tabulator';
import type { CellComponent } from 'tabulator-tables';
import type { RoadTrafficSummary, VehicleCountByTime } from '@/shared/types/types-statistic';
import { useColorStore } from '@/store/colorStore';
import { useThemeStore } from '@/store/themeStore';
import { lttbIndices } from '../utils/lttb';

const statisticByTypeTabulator = [
  {
    title: 'No',
    formatter: (cell: CellComponent) => {
      const row = cell.getRow();
      const page = row.getTable().getPage();
      const pageSize = row.getTable().getPageSize();
      return (page - 1) * pageSize + row.getPosition(true);
    },
    width: 60,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '집계시간',
    field: 'aggregated_time',
    hozAlign: 'center',
    widthGrow: 2.5,
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '사이트',
    field: 'site_name',
    widthGrow: 2,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '접근로',
    field: 'road_name',
    widthGrow: 2,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '차종 전체',
    field: 'all_cnt',
    width: 70,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '승용차',
    field: 'car_cnt',
    width: 70,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '승합차',
    field: 'van_cnt',
    width: 70,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '트럭',
    field: 'truck_cnt',
    width: 70,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '대형 트럭',
    field: 'long_truck_cnt',
    width: 70,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '버스',
    field: 'bus_cnt',
    width: 70,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '오토바이',
    field: 'motorcycle_cnt',
    width: 70,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '자전거',
    field: 'bicycle_cnt',
    width: 70,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '기타',
    field: 'unknown_cnt',
    width: 60,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
];

const StatisticByType = ({
  data,
  chartData,
}: {
  data?: RoadTrafficSummary[];
  chartData?: VehicleCountByTime[];
}) => {
  const { t } = useTranslation();
  const flatData = data?.flat(Infinity);
  const dataCnt = flatData?.map((item) => {
    const {
      aggregate_end_time,
      aggregate_start_time,
      aggregated_time,
      all_cnt,
      car_cnt,
      van_cnt,
      truck_cnt,
      long_truck_cnt,
      bus_cnt,
      motorcycle_cnt,
      bicycle_cnt,
      unknown_cnt,
      road_id,
      road_name,
      site_id,
      site_name,
    } = item;

    return {
      aggregate_end_time,
      aggregate_start_time,
      aggregated_time,
      all_cnt,
      car_cnt,
      van_cnt,
      truck_cnt,
      long_truck_cnt,
      bus_cnt,
      motorcycle_cnt,
      bicycle_cnt,
      unknown_cnt,
      road_id,
      road_name,
      site_id,
      site_name,
    };
  });

  const chartRecords = chartData as unknown as Record<string, unknown>[] | undefined;
  const dataCntChart: Record<string, (number | string)[]> | null = chartRecords?.length
    ? (Object.fromEntries(
        Object.keys(chartRecords[0] ?? {}).map((key) => [
          key,
          chartRecords.map((item) => item[key] ?? 0),
        ])
      ) as Record<string, (number | string)[]>)
    : null;

  const languageTabulator = () => {
    const datalanguage = {
      pagination: {
        first: t('cmn > first page'), //text for the first page button
        first_title: t('cmn > first page'), //tooltip text for the first page button
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
    return datalanguage;
  };

  const optionsTabulator = {
    pagination: true,
    paginationSize: 10,
    rowHeight: 41,
    movableRows: false,
    index: 'id',
    locale: 'ko',
    langs: {
      ko: languageTabulator(),
    },
    resizableRows: false,
    footerElement: `<div style="padding: 0 20px 0 0; text-align: right;">총 ${dataCnt?.length || 0} 건</div>`,
    placeholder: "<div style='text-align:center;'> No Data</div>",
  };

  const { apexChart: apexColors, palette } = useColorStore();
  // Theme is read here so the tooltip follows the current light/dark mode.
  const isDark = useThemeStore((state) => state.isDark);

  // Downsample to the chart's pixel width. All series share one x-axis, so pick
  // the kept buckets once (driven by all_cnt) and slice every series by them.
  const cellRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  useEffect(() => {
    const el = cellRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setChartWidth(Math.round(w / 50) * 50); // 50px steps to limit re-renders
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const pointBudget = chartWidth > 0 ? Math.max(100, chartWidth) : 1000;

  const times = (dataCntChart?.aggregate_start_time ?? []) as (string | number)[];
  const xs = times.map((t) => new Date(t as string).getTime());
  const keepIdx = lttbIndices(
    xs,
    (dataCntChart?.all_cnt ?? []).map((v) => Number(v) || 0),
    pointBudget
  );
  const pickSeries = (key: string) => {
    const vals = dataCntChart?.[key] ?? [];
    return keepIdx.map((i) => ({ x: xs[i] as number, y: Number(vals[i] ?? 0) }));
  };

  const options = {
    chart: {
      type: 'line',
      zoom: {
        enabled: false,
      },
      fontFamily: 'Pretendard GOV',
      toolbar: {
        show: false,
      },
      foreColor: apexColors.foreColor,
    },
    // Slot 0 = "전체" (aggregate total, neutral), slots 1..8 = 8 vehicle types.
    colors: [apexColors.seriesTotal, ...palette.category.slice(0, 8)],
    markers: {
      size: 4,
    },
    stroke: {
      show: true,
      curve: 'straight',
      width: 1.5,
    },
    grid: {
      borderColor: apexColors.gridBorderColor,
    },
    xaxis: {
      type: 'datetime',
      // Bucket timestamps are UTC ('...Z'); render their wall-clock as-is.
      labels: { rotate: 0, datetimeUTC: true },
    },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      x: { format: 'yyyy-MM-dd HH:mm' },
    },
  };

  const averageWaitTime = [
    { name: '전체', data: pickSeries('all_cnt') },
    { name: '승용차', data: pickSeries('car_cnt') },
    { name: '승합차', data: pickSeries('van_cnt') },
    { name: '트럭', data: pickSeries('truck_cnt') },
    { name: '대형 트럭', data: pickSeries('long_truck_cnt') },
    { name: '버스', data: pickSeries('bus_cnt') },
    { name: '오토바이', data: pickSeries('motorcycle_cnt') },
    { name: '자전거', data: pickSeries('bicycle_cnt') },
    { name: '기타', data: pickSeries('unknown_cnt') },
  ] as ApexAxisChartSeries;

  return (
    <section>
      <ReactTabulator
        data={dataCnt}
        columns={statisticByTypeTabulator as unknown as ColumnDefinition[]}
        layout={'fitColumns'}
        className="tabulator-custom w-full "
        //   pagination="local"
        options={optionsTabulator}
      />
      <div
        ref={cellRef}
        className="mt-[40px] flex flex-col border-[3px] rounded-[3px] p-[10px]"
        style={{ borderColor: 'var(--chart-border)' }}
      >
        <span className="title3bold" style={{ color: 'var(--chart-label)' }}>
          차종별 통행량
        </span>
        <Chart
          type="line"
          options={options as ApexOptions}
          series={averageWaitTime}
          height={350}
          className={'flex w-full !min-h-[0]'}
        />
      </div>
    </section>
  );
};

export default StatisticByType;
